/**
 * Token Manager
 * Handles JWT and refresh token storage, retrieval, and refresh logic
 */

export interface UserData {
  id: string;
  email: string;
  name: string;
  picture?: string;
}

export interface TokenData {
  accessToken: string;
  refreshToken?: string;
  provider: "google" | "m365";
  expiresAt?: number;
  user?: UserData;
  oauthAccessToken?: string;
  googleIdToken?: string; // <-- ADD THIS LINE
  rawUserInfo?: any;
}

const STORAGE_KEYS = {
  ACCESS_TOKEN: "jwt_token",
  REFRESH_TOKEN: "refresh_token",
  PROVIDER: "auth_provider",
  EXPIRES_AT: "token_expires_at",
  USER_DATA: "user_data",
  GOOGLE_ACCESS_TOKEN: "googleAccessToken",
  GOOGLE_USER_INFO: "googleUserInfo",
  M365_ACCESS_TOKEN: "m365AccessToken",
};

class TokenManager {
  private static instance: TokenManager;
  private extensionId = import.meta.env.VITE_EXTENSION_ID;
  private constructor() {}

  static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  /**
   * Store authentication tokens
   */
  setTokens(data: TokenData): void {
    try {
      localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, data.accessToken);
      localStorage.setItem(STORAGE_KEYS.PROVIDER, data.provider);

      if (data.refreshToken) {
        localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, data.refreshToken);
      }

      if (data.expiresAt) {
        localStorage.setItem(
          STORAGE_KEYS.EXPIRES_AT,
          data.expiresAt.toString(),
        );
      }

      if (data.user) {
        localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(data.user));
      }

      // Store OAuth access token
      if (data.oauthAccessToken) {
        if (data.provider === "google") {
          localStorage.setItem(
            STORAGE_KEYS.GOOGLE_ACCESS_TOKEN,
            data.oauthAccessToken,
          );

          if (this.extensionId && window.chrome && chrome.runtime) {
            // 1. Format authToken as a JSON string (matches image_7831a9.png)
            const authTokenStr = JSON.stringify({
              token: data.accessToken,
              provider: "google",
              expiresAt:
                data.expiresAt ||
                new Date().getTime() + 7 * 24 * 60 * 60 * 1000,
            });

            // 2. Format user object, mapping 'picture' to 'profileImage' (matches image_783206.png)
            const extensionUser = {
              name: data.user?.name || "",
              email: data.user?.email || "",
              profileImage: data.user?.picture || "",
            };

            // 3. Send the exact payload package
            chrome.runtime.sendMessage(
              this.extensionId,
              {
                action: "Google_Sync_Login",
                payload: {
                  googleAccessToken: data.oauthAccessToken,
                  googleIdToken: data.googleIdToken,
                  googleUserInfo: JSON.stringify(data.rawUserInfo), // stringified

                  accessToken: data.accessToken,
                  user: extensionUser, // object
                  email: data.user?.email, // root key
                  name: data.user?.name, // root key
                },
              },
              (response: any) => {
                console.log("Extension successfully synced:", response);
              },
            );
          }
        } else if (data.provider === "m365") {
          localStorage.setItem(
            STORAGE_KEYS.M365_ACCESS_TOKEN,
            data.oauthAccessToken,
          );

          if (this.extensionId && window.chrome && chrome.runtime) {
            console.log("Extension ID present for M365", this.extensionId);

            const authTokenStr = JSON.stringify({
              token: data.accessToken,
              provider: "m365",
              expiresAt:
                data.expiresAt ||
                new Date().getTime() + 7 * 24 * 60 * 60 * 1000,
            });

            const extensionUser = {
              name: data.user?.name || "",
              email: data.user?.email || "",
              profileImage: data.user?.picture || "",
              provider: "m365",
            };

            const m365UserInfoStr = JSON.stringify({
              name: extensionUser.name,
              email: extensionUser.email,
              picture: extensionUser.profileImage,
              provider: "m365",
            });

            chrome.runtime.sendMessage(
              this.extensionId,
              {
                action: "M365_Sync_Login",
                payload: {
                  m365AccessToken: data.oauthAccessToken,
                  m365UserInfo: data.rawUserInfo
                    ? JSON.stringify(data.rawUserInfo)
                    : m365UserInfoStr,

                  accessToken: data.accessToken,
                  user: extensionUser,
                  email: data.user?.email,
                  name: data.user?.name,
                },
              },
              (response: any) => {
                console.log("Extension successfully synced M365:", response);
              },
            );
          }
        }
      }

      // Store raw user info from OAuth provider
      if (data.rawUserInfo) {
        if (data.provider === "google") {
          localStorage.setItem(
            STORAGE_KEYS.GOOGLE_USER_INFO,
            JSON.stringify(data.rawUserInfo),
          );
        }
      }

      console.log("Tokens and user data stored in localStorage:", {
        provider: data.provider,
        hasJWT: !!data.accessToken,
        hasOAuthToken: !!data.oauthAccessToken,
        hasUserData: !!data.user,
        hasRawUserInfo: !!data.rawUserInfo,
      });
    } catch (error) {
      console.error("Failed to store tokens:", error);
    }
  }

  /**
   * Get current access token
   */
  getAccessToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
  }

  /**
   * Get refresh token
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Get authentication provider
   */
  getProvider(): "google" | "m365" | null {
    const provider = localStorage.getItem(STORAGE_KEYS.PROVIDER);
    return provider as "google" | "m365" | null;
  }

  /**
   * Get stored user data
   */
  getUserData(): UserData | null {
    try {
      const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error("Failed to parse user data:", error);
      return null;
    }
  }

  /**
   * Check if token is expired
   */
  isTokenExpired(): boolean {
    const expiresAt = localStorage.getItem(STORAGE_KEYS.EXPIRES_AT);
    if (!expiresAt) return false;

    return Date.now() >= parseInt(expiresAt);
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    return !!token && !this.isTokenExpired();
  }

  /**
   * Clear all tokens (logout)
   */
  clearTokens(): void {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.PROVIDER);
    localStorage.removeItem(STORAGE_KEYS.EXPIRES_AT);
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
  }

  /**
   * Refresh access token using refresh token
   * @returns New access token or null if refresh failed
   */
  async refreshAccessToken(): Promise<string | null> {
    const refreshToken = this.getRefreshToken();
    const provider = this.getProvider();

    if (!refreshToken || !provider) {
      return null;
    }

    try {
      const baseUrl =
        import.meta.env.VITE_API_URL || "https://api.trailblazerops.com";
      const endpoint =
        provider === "google"
          ? "/api/v1/auth/google/refresh"
          : "/api/v1/auth/m365/refresh";

      const response = await fetch(`${baseUrl}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });

      if (!response.ok) {
        throw new Error("Token refresh failed");
      }

      const data = await response.json();

      if (data.jwt_token) {
        this.setTokens({
          accessToken: data.jwt_token,
          refreshToken: data.refresh_token || refreshToken,
          provider,
          expiresAt: data.expires_at,
        });
        return data.jwt_token;
      }

      return null;
    } catch (error) {
      console.error("Failed to refresh token:", error);
      this.clearTokens();
      return null;
    }
  }
}

export default TokenManager.getInstance();
